// Training partner card on the Mates tab: one mate you keep each other on
// track with. Each of you has your own weekly target; a week counts when you
// both hit yours, and counted weeks in a row are the partner streak. One
// freeze a month covers a missed week. Rules: api/lib/partners.js.

import React, { useCallback, useEffect, useState } from "react";
import { Handshake, Loader2, Snowflake } from "lucide-react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import { DeleteConfirm, Group } from "../profile/ProfileParts";
import HelpLink from "../help/HelpLink";
import Avatar from "./Avatar";
import * as api from "../../services/api";
import { useSocial } from "../../contexts/SocialContext";
import { useToast } from "../../contexts/ToastContext";
import { cn } from "../../lib/utils";
import { describeFreeze, describeStreak, describeWeek, firstName, weekPct } from "../../utils/partners";

const row = "grid w-full items-center gap-2.5 px-3 py-2.5 text-left";

const dayLabel = (iso) => new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });

/** One person's week: name, a bar, "2 of 3 days". */
const WeekBar = ({ label, side }) => {
  const done = side.days >= side.target;
  return (
    <div className="grid grid-cols-[4.5rem_minmax(0,1fr)_auto] items-center gap-2.5">
      <span className="truncate text-[13px]">{label}</span>
      <span className="h-2 overflow-hidden rounded-full bg-muted">
        <span
          className={cn("block h-full rounded-full", done ? "bg-primary" : "bg-primary/50")}
          style={{ width: `${weekPct(side)}%` }}
        />
      </span>
      <span className="text-xs tabular-nums text-muted-foreground">
        {Math.min(side.days, 7)} of {side.target}
      </span>
    </div>
  );
};

/** The last eight weeks as dots: counted, frozen, or missed. */
const History = ({ history }) =>
  history.length > 0 ? (
    <div className="flex items-center gap-1.5" aria-label="Recent weeks">
      {history.map((w) => (
        <span
          key={w.weekStart}
          title={`Week of ${dayLabel(w.weekStart)}: ${w.outcome === "met" ? "counted" : w.outcome}`}
          className={cn(
            "flex h-4 w-4 items-center justify-center rounded-full",
            w.outcome === "met" && "bg-primary",
            w.outcome === "frozen" && "border border-sky-400/60 text-sky-400",
            w.outcome === "missed" && "border border-muted-foreground/40",
          )}
        >
          {w.outcome === "frozen" && <Snowflake className="h-2.5 w-2.5" />}
        </span>
      ))}
    </div>
  ) : null;

export default function PartnerCard() {
  const { friends = [], partnerVersion } = useSocial();
  const { showToast } = useToast();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pickOpen, setPickOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await api.getPartners());
    } catch {
      setState({ partnership: null, incoming: [], outgoing: [], error: true });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, partnerVersion]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      await load();
      if (message) showToast({ title: message });
      return true;
    } catch (e) {
      showToast({ title: "That didn't work", description: e.message });
      return false;
    } finally {
      setBusy(null);
    }
  };

  if (!state || state.error) return null;
  const { partnership: p, incoming = [], outgoing = [] } = state;
  // Nothing to show until there's a mate to pair with.
  if (!p && !incoming.length && !outgoing.length && friends.length === 0) return null;

  const requests = incoming.map((r) => (
    <div key={r.id} className={`${row} grid-cols-[2rem_minmax(0,1fr)_auto]`}>
      <Avatar name={r.partner.name} photo={r.partner.photo_url} />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{r.partner.name}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">Wants to be training partners</span>
      </span>
      <span className="flex gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="text-muted-foreground"
          disabled={busy === r.id}
          onClick={() => act(r.id, () => api.endPartner(r.id))}
        >
          Decline
        </Button>
        <Button
          size="sm"
          disabled={busy === r.id || Boolean(p)}
          onClick={() => act(r.id, () => api.acceptPartner(r.id), `You and ${firstName(r.partner.name)} are training partners`)}
        >
          Accept
        </Button>
      </span>
    </div>
  ));

  const waiting = outgoing.map((r) => (
    <div key={r.id} className={`${row} grid-cols-[2rem_minmax(0,1fr)_auto]`}>
      <Avatar name={r.partner.name} photo={r.partner.photo_url} />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{r.partner.name}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">Asked to be training partners</span>
      </span>
      <Button
        size="sm"
        variant="ghost"
        className="text-muted-foreground"
        disabled={busy === r.id}
        onClick={() => act(r.id, () => api.endPartner(r.id))}
      >
        Cancel
      </Button>
    </div>
  ));

  const card = p && (
    <>
      <button
        type="button"
        onClick={() => setManageOpen(true)}
        className={`${row} grid-cols-[2.5rem_minmax(0,1fr)_auto] transition-colors hover:bg-muted/30`}
      >
        <Avatar name={p.partner.name} photo={p.partner.photo_url} className="h-10 w-10 text-sm" />
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{p.partner.name}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {p.since ? `Partners since ${dayLabel(p.since)}` : "Training partners"}
          </span>
        </span>
        <span className="text-right">
          <span className="flex items-center justify-end gap-1 text-xl font-semibold tabular-nums">
            <Handshake className="h-4 w-4 text-primary" />
            {p.streakWithThisWeek}
          </span>
          <span className="block text-[11px] text-muted-foreground">
            {p.streakWithThisWeek === 1 ? "week" : "weeks"} in a row
          </span>
        </span>
      </button>
      {p.thisWeek && (
        <div className="grid gap-2 px-3 py-3">
          <WeekBar label="You" side={p.thisWeek.me} />
          <WeekBar label={firstName(p.partner.name)} side={p.thisWeek.them} />
          <p className="pt-0.5 text-[13px] leading-snug">{describeWeek(p.thisWeek, p.partner.name)}</p>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 px-3 py-2.5">
        <History history={p.history} />
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Snowflake className="h-3.5 w-3.5 text-sky-400" />
          {describeFreeze(p.freezeAvailable, p.thisWeek?.weekStart)}
        </span>
      </div>
    </>
  );

  const invite = !p && !outgoing.length && !incoming.length && friends.length > 0 && (
    <div className="grid gap-2.5 px-3 py-3">
      <p className="text-[13px] leading-snug text-muted-foreground">
        Pick a mate to keep each other on track. A week counts when you both hit your own weekly target.
      </p>
      <Button variant="outline" onClick={() => setPickOpen(true)}>
        <Handshake className="mr-2 h-4 w-4" /> Choose a training partner
      </Button>
    </div>
  );

  return (
    <>
      <Group title="Training partner" note={p && p.bestStreak > 1 ? `Best: ${p.bestStreak} weeks` : undefined}>
        {requests}
        {card}
        {waiting}
        {invite}
      </Group>

      <DetailSheet open={pickOpen} onOpenChange={setPickOpen} title="Choose a training partner">
        <p className="text-[13px] leading-snug text-muted-foreground">
          They&rsquo;ll get a request. Once they accept, you each train to your own weekly target from Profile, and a
          week counts when you both make it.{" "}
          <HelpLink topic="social#training-partners" label="How it works" />
        </p>
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
          {friends.map((f) => (
            <button
              key={f.id}
              type="button"
              disabled={Boolean(busy)}
              onClick={async () => {
                const ok = await act(f.id, () => api.askPartner(f.id), `Asked ${firstName(f.name)}`);
                if (ok) setPickOpen(false);
              }}
              className={`${row} grid-cols-[2rem_minmax(0,1fr)_auto] transition-colors hover:bg-muted/30`}
            >
              <Avatar name={f.name} photo={f.photo_url} />
              <span className="truncate text-sm font-medium">{f.name}</span>
              {busy === f.id ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <span className="text-xs font-semibold text-primary">Ask</span>
              )}
            </button>
          ))}
        </div>
      </DetailSheet>

      {p && (
        <DetailSheet
          open={manageOpen}
          onOpenChange={(o) => {
            setManageOpen(o);
            if (!o) setConfirming(false);
          }}
          title="Training partner"
        >
          <ul className="grid gap-1.5 text-[13px] leading-snug text-muted-foreground">
            <li>A week counts when you both hit your own target. Weeks run Monday to Sunday.</li>
            <li>Targets are locked in on Monday. Change yours in Profile and it applies from next week.</li>
            <li>One freeze a month covers a missed week, used for you automatically.</li>
            <li>You&rsquo;re both nudged on Friday if either of you is behind.</li>
          </ul>
          <p className="text-sm">
            Best run together: <span className="font-semibold">{describeStreak(p.bestStreak)}</span>
          </p>
          <DeleteConfirm
            action="End"
            name={`your partnership with ${firstName(p.partner.name)}`}
            consequence="Your streak stops here. Badges you've earned stay."
            confirming={confirming}
            onAsk={() => setConfirming(true)}
            onCancel={() => setConfirming(false)}
            onConfirm={async () => {
              const ok = await act("end", () => api.endPartner(p.id), "Partnership ended");
              if (ok) setManageOpen(false);
            }}
            busy={busy === "end"}
          />
        </DetailSheet>
      )}
    </>
  );
}
